import React, { useEffect, useState } from "react";
import {
  Customer,
  getAllActiveCustomers,
  getCustomersByApplicationId,
} from "@/api/customers";
import {
  Application,
  ApplicationDefaultType,
  getApplication,
  getApplicationDefaultTypes,
} from "@/api/applications";
import AddButton from "@/ui-controls/AddButton";
import AppCustomerDetailList from "@/components/customers/AppCustomerDetailList";
import NewAppCustomer from "@/components/customers/NewAppCustomer";
import SearchBox from "@/ui-controls/SearchBox";
import { useNavigate } from "react-router-dom";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import { useErrorNotification } from "@/utils/useErrorNotification";

interface Props {
  ApplicationId: number;
  ShowOnlyNewAppCustomerPanel: boolean;
  onCloseNewAppCustomerPanel?: () => any;
  onSaveAppCustomers?: (customers: Customer[]) => any;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
  onApplicationLoaded?: (name: string) => void;
}

const AppCustomers = (props: Props) => {
  const navigate = useNavigate();
  const { errorMessage, showError, clearError } = useErrorNotification();
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [appCustomers, setAppCustomers] = useState<Customer[]>([]);
  const [nonAppCustomers, setNonAppCustomers] = useState<Customer[]>([]);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [applicationDefaultTypes, setApplicationDefaultTypes] = useState<
    ApplicationDefaultType[]
  >([]);
  const [loadingAllCustomers, setLoadingAllCustomers] =
    useState<boolean>(false);
  const [loadingAppCustomers, setLoadingAppCustomers] =
    useState<boolean>(false);
  const [loadingApplication, setLoadingApplication] = useState<boolean>(false);
  const [newAppCustomerPanelOpenned, setNewAppCustomerPanelOpenned] =
    useState<boolean>(props.ShowOnlyNewAppCustomerPanel);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [isSearchPerformed, setIsSearchPerformed] = useState<boolean>(false);

  useEffect(() => {
    getAllCustomers();
    return () => {};
  }, []);

  useEffect(() => {
    getAppCustomers();
    getApplicationById();
    getApplicationDefaultTypesForApp();
  }, [props.ApplicationId]);

  useEffect(() => {
    getNonAppCustomers();
    handleCustomerSearch("");
  }, [appCustomers, allCustomers]);

  const getAllCustomers = () => {
    setLoadingAllCustomers(true);
    getAllActiveCustomers()
      .then((result) => {
        setAllCustomers(result);
        setLoadingAllCustomers(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingAllCustomers(false);
      });
  };

  const getAppCustomers = () => {
    const appId: number = Number(props.ApplicationId);

    setLoadingAppCustomers(true);
    getCustomersByApplicationId(appId)
      .then((result) => {
        setAppCustomers(result);
        setLoadingAppCustomers(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingAppCustomers(false);
      });
  };

  const getNonAppCustomers = () => {
    const customers = allCustomers.filter(
      (c) => !appCustomers.find((ac) => ac.Id === c.Id)
    );

    setNonAppCustomers(customers);
  };

  const getApplicationById = () => {
    const appId: number = Number(props.ApplicationId);

    setLoadingApplication(true);
    getApplication(appId)
      .then((result) => {
        setApplication(result);
        if (props.onApplicationLoaded && result?.Name) {
          props.onApplicationLoaded(result.Name);
        }
        setLoadingApplication(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingApplication(false);
      });
  };

  const getApplicationDefaultTypesForApp = () => {
    const appId: number = Number(props.ApplicationId);

    getApplicationDefaultTypes(appId)
      .then((result) => {
        setApplicationDefaultTypes(result || []);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleError = (error: any) => {
    showError(error);
  };

  const handleCustomerSearch = (searchValue: string) => {
    const hasSearchValue = searchValue.trim().length > 0;
    setIsSearchPerformed(hasSearchValue);

    if (hasSearchValue) {
      const filteredCustomers = appCustomers.filter((customer) =>
        customer.Name.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredCustomers(filteredCustomers);
    } else {
      setFilteredCustomers([]);
    }
  };

  const handleRefresh = () => {
    setIsSearchPerformed(false);
    setFilteredCustomers([]);
    getAppCustomers();
    getApplicationDefaultTypesForApp();
  };

  return (
    <>
      {!props.ShowOnlyNewAppCustomerPanel ? (
        <>
          <div className="page-header">
            <div className="breadcrumb-left-section">
              <IconButton
                iconProps={{ iconName: "Back" }}
                title="Back to Applications"
                onClick={() => navigate("/applications")}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: { fontSize: 18, color: "#0078d4" },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                }}
              />
              <div>
                {props.breadcrumbTitle && (
                  <h2 className="page-breadcrumb-title">
                    {props.breadcrumbTitle}
                  </h2>
                )}
                {(props.breadcrumbSubtitle || application?.Name) && (
                  <div style={{ color: "#605e5c", fontSize: "14px" }}>
                    {application?.Name ? (
                      <>
                        Manage customers for application{" "}
                        <strong>{application.Name}</strong>
                      </>
                    ) : (
                      props.breadcrumbSubtitle
                    )}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <AddButton
                text="Customer"
                onClick={() => setNewAppCustomerPanelOpenned(true)}
              />
              <SearchBox placeholder="Search" onSearch={handleCustomerSearch} />
              <IconButton
                iconProps={{ iconName: "Refresh" }}
                title="Refresh"
                onClick={handleRefresh}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: { fontSize: 18, color: "#0078d4" },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                }}
              />
            </div>
          </div>
          <div>
            {application ? (
              <AppCustomerDetailList
                CustomerList={(() => {
                  return isSearchPerformed
                    ? filteredCustomers.length === 0
                      ? []
                      : filteredCustomers
                    : appCustomers;
                })()}
                Application={application}
                hasDefaultTypes={applicationDefaultTypes.length > 0}
                defaultTypes={applicationDefaultTypes}
                onRemoveCustomers={() => {
                  getAppCustomers();
                }}
                onDelete={(customer) => {
                  getAppCustomers();
                  setAllCustomers((prev) =>
                    prev.filter((c) => c.Id !== customer.Id)
                  );
                }}
              />
            ) : null}
          </div>
        </>
      ) : null}
      <NewAppCustomer
        application={application}
        nonAppCustomers={nonAppCustomers}
        allCustomers={allCustomers}
        isPanelOpenned={newAppCustomerPanelOpenned}
        onPanelClosed={() => {
          setNewAppCustomerPanelOpenned(false);
          if (props.onCloseNewAppCustomerPanel)
            props.onCloseNewAppCustomerPanel();
        }}
        onSaveNewCustomer={(newCustomer) => {
          setAllCustomers((prev) => [...prev, newCustomer]);
          getAppCustomers();
        }}
        onSave={(_) => getAppCustomers()}
      />
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={clearError}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default AppCustomers;
